import {
  FlexProps,
  Popover,
  PopoverContentProps,
  PopoverRootProps,
  Portal,
  UseDisclosureProps,
} from '@chakra-ui/react';
import React from 'react';

import FilterPillButton from '@components/common/FilterPillButton';
import useControlledPopover from 'hooks/useControlledPopover';
import Arrowhead from 'vectors/Arrowhead';

interface FilterPillPopoverProps extends UseDisclosureProps {
  buttonContent: React.ReactNode;
  isActive?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  isDefaultState?: boolean;
  isDisabled?: boolean;
  testId?: string;
  contentProps?: PopoverContentProps;
  positioning?: PopoverRootProps['positioning'];
  buttonBorderRadius?: FlexProps['borderRadius'];
}

const FilterPillPopover: React.FC<FilterPillPopoverProps> = React.memo(
  ({
    buttonContent,
    leftIcon,
    rightIcon,
    isActive = true,
    isDefaultState = false,
    isDisabled,
    children,
    testId,
    contentProps: popoverProps,
    buttonBorderRadius,
    positioning,
    ...disclosure
  }) => {
    const { open, onToggle, triggerRef, contentRef } = useControlledPopover<
      HTMLDivElement,
      HTMLButtonElement
    >();
    const isHighlighted = isActive && !isDefaultState;

    return (
      <Popover.Root
        lazyMount
        {...disclosure}
        open={open}
        onOpenChange={onToggle}
        positioning={positioning}
      >
        <Popover.Trigger asChild ref={triggerRef}>
          <FilterPillButton
            isHighlighted={isHighlighted}
            isSelected={open}
            isDisabled={isDisabled ?? false}
            testId={testId}
            leftIcon={leftIcon}
            rightIcon={rightIcon ?? <Arrowhead boxSize={2} direction="down" />}
            borderRadius={buttonBorderRadius}
          >
            {buttonContent}
          </FilterPillButton>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content ref={contentRef} {...popoverProps}>
              {children}
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    );
  },
);

FilterPillPopover.displayName = 'FilterPillPopover';

export default FilterPillPopover;
